<template>
  <div class="dialog-add-vessel-document">
    <vs-prompt
      @accept="addVesselDocument"
      @cancel="cancel"
      @close="close"
      :active.sync="showDialog"
      :title="title"
      :accept-text="acceptText"
      :cancel-text="cancelText"
      :is-valid="validateForm"
      :buttons-hidden="buttonsHidden"
    >
      <div>
        <span>書類名</span>
        <vs-input
          v-model="vesselDocumentRequest.document_name"
          class="w-full"
          v-validate="'required|max:32'"
          name="name"
          data-vv-as="書類名"
        />
        <span class="text-danger text-sm" v-show="errors.has('name')">{{
          errors.first("name")
        }}</span>
        <br />
        <div style="padding-top: 10px">
          <p>ファイルの選択</p>
          <div v-show="showPdf" class="full-area div-pdf">
            <pdf :src="image.src" class="pdf-area" />
          </div>
          <div v-show="showImg" class="full-area pt-10 div-office">
            <img :src="image.src" class="image-area" />
          </div>
          <div v-show="showOffice" class="full-area div-office">
            <p><img :src="officeIcon" />{{ officeFileName }}</p>
          </div>
          <div
            v-if="!image.src"
            :class="[isDrag ? dragoverClass : dragleaveClass]"
            @dragover.prevent="checkDrag($event, true)"
            @dragleave.prevent="checkDrag($event, false)"
            @drop.prevent="handleFileSelect($event)"
            @click="$refs.update_photo_input.click()"
          >
            <input
              type="file"
              ref="update_photo_input"
              class="hidden"
              @change="handleFileSelect($event)"
            />
            <span
              >ファイルをドロップ、<br />またはクリックしてファイルを選択<br />
              <P class="text-sm mt-2"
                >最大アップロードサイズ：<strong>5MB</strong></P
              ></span
            >
          </div>
          <span class="text-danger text-sm" v-show="error.message">{{
            error.message
          }}</span>
          <div v-show="showNotesMsg" class="mt-2">
            <P class="text-sm">アップロードできるファイル形式：</P>
            <P class="text-sm"
              ><strong>{{ supportedFileFormats }}</strong></P
            >
          </div>
          <br />
        </div>
      </div>
    </vs-prompt>
  </div>
</template>

<script>
import { AiseaApiFactory } from "@/api/aisea/apiFactory";
const EnterpriseVesselDocument = AiseaApiFactory.get("enterpriseVesselDocument");

import { FileUtil, UnitUtil } from "@/utils";
import { createNamespacedHelpers } from "vuex";
import pdf from "vue-pdf";

const { mapActions } = createNamespacedHelpers("enterpriseVesselView");

const SUPPORTED_CONTENT_TYPE = {
  IMAGE: "image/",
  PDF: "application/pdf",
  OFFICE: {
    XLS: "application/vnd.ms-excel",
    XLSX: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    XLSM: "application/vnd.ms-excel.sheet.macroEnabled.12",
    XLSM_OSX: "application/vnd.ms-excel.sheet.macroenabled.12",
    DOC: "application/msword",
    DOCX:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    DOCM: "application/vnd.ms-word.document.macroEnabled.12",
    DOCM_OSX: "application/vnd.ms-word.document.macroenabled.12"

  }
};

const SUPPORTED_EXTENSIONS = [
  ".png",
  ".jpg",
  ".jpeg",
  ".pdf",
  ".xls",
  ".xlsx",
  ".xlsm",
  ".doc",
  ".docx",
  ".docm"
];

const MAXIMUM_FILE_SIZE = 5242880;

export default {
  components: { pdf },
  data() {
    return {
      vesselId: null,
      vesselDocumentRequest: {
        delivery_date: null,
        document_name: null,
        expired_date: null,
        picture_id: null
      },
      image: {
        src: null,
        type: null
      },
      fileIcon: require("@/assets/images/icons/office/excel.svg"),
      error: { message: null },
      showDialog: false,
      title: "船舶書類の追加",
      acceptText: "追加",
      cancelText: "キャンセル",
      pictureData: {},
      pictureUrl: null,
      cropImg: null,
      isValid: false,
      isDrag: false,
      isCrop: null,
      buttonsHidden: false,
      dragleaveClass: "drag-area-dragleave",
      dragoverClass: "drag-area-dragover",
      showOffice: false,
      showNotesMsg: true,
      officeIcon: null,
      officeFileName: null
    };
  },
  computed: {
    showImg() {
      if (!this.image.src) return false;
      return this.image.type.startsWith(SUPPORTED_CONTENT_TYPE.IMAGE);
    },
    showPdf() {
      if (!this.image.src) return false;
      return this.image.type === SUPPORTED_CONTENT_TYPE.PDF;
    },
    validateForm() {
      return (
        this.image.src !== null &&
        this.vesselDocumentRequest.document_name !== null &&
        !this.errors.any()
      );
    },
    supportedFileFormats() {
      return SUPPORTED_EXTENSIONS.join(" ");
    }
  },
  methods: {
    ...mapActions(["fetchDocuments", "addDocuments"]),

    show(vesselId) {
      this.vesselId = vesselId;
      this.showDialog = true;
    },

    validateInputFile(file) {
      if (this.isAvailableFileType(file)) {
        this.image.type = file.type;
        this.fileSIze = file.size;
        if (this.fileSIze >= MAXIMUM_FILE_SIZE) {
          this.error.message = `${file.name}(${UnitUtil.formatBytes(
            this.fileSIze,
            1
          )})は最大アップロードサイズ5MBを超えてます`;
          return false;
        }
        this.error.message = null;
        return true;
      } else {
        this.error.message = `${file.name}このファイル形式はアップロードできません`;
        return false;
      }
    },

    isAvailableFileType(file) {
      if (file.type.startsWith(SUPPORTED_CONTENT_TYPE.IMAGE)) {
        return true;
      }

      if (file.type.startsWith(SUPPORTED_CONTENT_TYPE.PDF)) {
        return true;
      }

      for (const [key, value] of Object.entries(
        SUPPORTED_CONTENT_TYPE.OFFICE
      )) {
        if (file.type === value) {
          this.officeFileName = file.name;
          this.officeIcon = key.startsWith("XL")
            ? require("@/assets/images/icons/office/excel.svg")
            : require("@/assets/images/icons/office/word.svg");
          return true;
        }
      }

      return false;
    },

    async addVesselDocument() {
      this.$vs.loading();
      let file = FileUtil.toBlob(this.image.src, this.image.type);
      let payload = {
        vesselId: this.vesselId,
        name: this.vesselDocumentRequest.document_name,
        file: file
      };
      try {
        await this.addDocuments(payload);
        await this.fetchDocuments(this.vesselId);
        this.$vs.notify({
          title:
            "「" +
            this.vesselDocumentRequest.document_name +
            "」を追加しました",
          color: "success",
          iconPack: "feather",
          icon: "icon-check"
        });
        this.$vs.loading.close();
        this.close();
      } catch (e) {
        this.$vs.notify({
          title: "船舶書類の登録に失敗しました",
          iconPack: "feather",
          icon: "icon-alert-circle",
          color: "danger"
        });
        this.$vs.loading.close();
        this.close();
      }
    },

    close() {
      this.cleanUp();
    },

    cancel() {
      this.cleanUp();
    },

    checkDrag(e, isDrag) {
      this.isDrag = isDrag;
    },

    handleFileSelect(e) {
      const file =
        typeof e.target.files !== "undefined"
          ? e.target.files[0]
          : e.dataTransfer.files[0];
      if (!this.validateInputFile(file)) {
        this.showNotesMsg = false;
        return;
      }
      let reader = new FileReader();
      reader.onload = e => {
        this.image.src = e.target.result;
        this.showNotesMsg = false;
        if (this.officeFileName) {
          this.showOffice = true;
        }
      };
      reader.readAsDataURL(file);
    },

    callEnterpriseVesselDocumentApi() {
      return new Promise((resolve, reject) => {
        EnterpriseVesselDocument.add(
          this.vesselId,
          this.vesselDocumentRequest
        )
          .then(response => {
            return resolve(response);
          })
          .catch(error => {
            reject(error);
          });
      });
    },

    onImageUpdated() {
      this.$emit("onImageUpdated", {
        data: this.pictureData,
        url: this.pictureUrl
      });
      this.cleanUp();
    },

    cleanUp() {
      this.$validator.pause();
      this.showDialog = false;
      this.image = {
        src: null,
        type: null
      };
      this.isValid = false;
      this.showNotesMsg = true;
      this.error.message = null;
      this.buttonsHidden = false;
      this.vesselId = null;
      this.vesselDocumentRequest.document_name = null;
      this.showOffice = false;
      this.officeFileName = null;
      this.officeIcon = null;
    }
  }
};
</script>

<style lang="scss">
.image-area {
  width: 100%;
  min-width: 200px;
  height: 200px;
  min-height: 200px;
  border-radius: 8px;
  filter: drop-shadow(0px 0px 5px rgba(0, 0, 0, 0.4));
  object-fit: scale-down;
}
span > canvas {
  height: 200px !important;
  width: auto !important;
}
.pdf-area {
  align-items: center;
  border-radius: 8px;
  filter: drop-shadow(0px 0px 5px rgba(0, 0, 0, 0.4));
  object-fit: scale-down;
}
.drag-area-dragleave {
  width: 100%;
  min-width: 200px;
  height: 100%;
  min-height: 200px;
  border: 2px dotted rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  background-color: #f5f5f5;
  text-align: center;
}
.drag-area-dragover {
  width: 100%;
  min-width: 200px;
  height: 100%;
  min-height: 200px;
  border: 2px solid rgba(115, 103, 240, 1);
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  background-color: #f5f5f5;
  text-align: center;
}
.div-pdf {
  padding-top: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
}
.div-office {
  padding-top: 10px;
  p {
    margin-left: 2.8em;
    text-indent: -2.8em;
    font-size: 86%;
    line-height: 1.6em;
  }
  p img {
    margin-right: 7px;
    height: 24px;
    vertical-align: bottom;
  }
}
</style>
